import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../i18n';
import { request } from '../../shared/services/api';

interface OCRResultData {
  documentoId: string;
  versionId: string;
  titulo: string;
  ocrResult: {
    text: string;
    confidence: number;
    pages: { pageNum: number; text: string; confidence: number }[];
    language: string;
    processingTimeMs: number;
  };
}

interface ProcesoOption {
  id: string;
  codigo: string;
  nombre: string;
}

const ACCEPTED = '.pdf,.png,.jpg,.jpeg,.tiff,.tif,.webp';

export function OCRIngestPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [procesoId, setProcesoId] = useState('');
  const [tipoDocumental, setTipoDocumental] = useState('RE');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<OCRResultData | null>(null);
  const [error, setError] = useState('');
  const [procesos, setProcesos] = useState<ProcesoOption[]>([]);
  const [procesosLoaded, setProcesosLoaded] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const loadProcesos = useCallback(async () => {
    if (procesosLoaded) return;
    try {
      const res = await request<{ data: ProcesoOption[] }>('/procesos?porPagina=100');
      setProcesos(res.data);
      setProcesosLoaded(true);
    } catch {
      // fallback
    }
  }, [procesosLoaded]);

  useState(() => {
    loadProcesos();
  });

  const handleFile = (f: File) => {
    setFile(f);
    setResult(null);
    setError('');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!file || !procesoId) return;
    setLoading(true);
    setProgress(10);
    setError('');
    setResult(null);

    try {
      setProgress(30);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('procesoId', procesoId);
      formData.append('tipoDocumental', tipoDocumental);

      setProgress(50);
      const res = await request<{ data: OCRResultData }>('/ocr/ingest', {
        method: 'POST',
        body: formData,
        headers: {},
      });

      setProgress(100);
      setResult(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al procesar el documento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1F3864', marginBottom: 24 }}>
        {t.tech?.ocr?.title ?? 'OCR / Digitalizacion'}
      </h2>

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#33BCE1' : '#D1D5DB'}`,
          borderRadius: 12,
          padding: 40,
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? '#F0F9FF' : '#FAFAFA',
          transition: 'all 0.2s',
          marginBottom: 20,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          style={{ display: 'none' }}
        />
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9CA3AF"
          strokeWidth="1.5"
          style={{ marginBottom: 8 }}
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div style={{ fontSize: 14, color: '#6B7280' }}>
          {file
            ? file.name
            : (t.tech?.ocr?.dropzone ?? 'Arrastra un archivo o haz clic para seleccionar')}
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
          PDF, PNG, JPG, TIFF, WebP (max 50 MB)
        </div>
      </div>

      {/* Selectors */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 6,
            }}
          >
            {t.tech?.ocr?.proceso ?? 'Proceso'}
          </label>
          <select
            value={procesoId}
            onChange={(e) => setProcesoId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D1D5DB',
              fontSize: 14,
            }}
          >
            <option value="">Seleccionar proceso...</option>
            {procesos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} — {p.nombre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              display: 'block',
              marginBottom: 6,
            }}
          >
            {t.tech?.ocr?.tipo ?? 'Tipo documental'}
          </label>
          <select
            value={tipoDocumental}
            onChange={(e) => setTipoDocumental(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D1D5DB',
              fontSize: 14,
            }}
          >
            <option value="RE">RE — Reporte</option>
            <option value="IT">IT — Instruccion Tecnica</option>
            <option value="MN">MN — Manual</option>
            <option value="FO">FO — Formato</option>
            <option value="NC">NC — Normativa</option>
          </select>
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={loading || !file || !procesoId}
        style={{
          width: '100%',
          padding: '12px 24px',
          borderRadius: 8,
          border: 'none',
          background: loading || !file || !procesoId ? '#9CA3AF' : '#1F3864',
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          cursor: loading || !file || !procesoId ? 'not-allowed' : 'pointer',
          marginBottom: 20,
        }}
      >
        {loading
          ? (t.tech?.ocr?.processing ?? 'Procesando OCR...')
          : (t.tech?.ocr?.submit ?? 'Digitalizar')}
      </button>

      {/* Progress */}
      {loading && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ background: '#E5E7EB', borderRadius: 4, height: 8, overflow: 'hidden' }}>
            <div
              style={{
                background: '#33BCE1',
                height: '100%',
                width: `${progress}%`,
                transition: 'width 0.5s ease',
                borderRadius: 4,
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4, textAlign: 'center' }}>
            {progress < 50
              ? 'Extrayendo texto...'
              : progress < 100
                ? 'Creando documento...'
                : 'Completado'}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            padding: 12,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            color: '#DC2626',
            fontSize: 13,
            marginBottom: 20,
          }}
        >
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div
          style={{
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 10,
            padding: 20,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#166534' }}>
              Documento creado
            </h3>
            <button
              onClick={() => navigate(`/process/documents/${result.documentoId}`)}
              style={{
                padding: '6px 16px',
                borderRadius: 6,
                border: '1px solid #166534',
                background: 'transparent',
                color: '#166534',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Ver documento
            </button>
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{result.titulo}</div>
          <div
            style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6B7280', marginBottom: 12 }}
          >
            <span>Confianza: {Math.round(result.ocrResult.confidence * 100)}%</span>
            <span>Paginas: {result.ocrResult.pages.length}</span>
            <span>Tiempo: {result.ocrResult.processingTimeMs}ms</span>
          </div>
          <details>
            <summary style={{ fontSize: 13, color: '#374151', cursor: 'pointer', fontWeight: 500 }}>
              Preview del texto extraido
            </summary>
            <pre
              style={{
                marginTop: 8,
                padding: 12,
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {result.ocrResult.text.substring(0, 2000)}
              {result.ocrResult.text.length > 2000 ? '\n...(truncado)' : ''}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
